import fs from 'node:fs/promises'
import path from 'node:path'
import { text } from '@clack/prompts'
import as from 'ansis'
import { Command } from 'commander'
import { n } from 'neverpanic'

import { ConfigVersion } from '@scalar-org/entities/project'
import { legacyTo2_0_0 } from '@scalar-org/scalar-config'

import { readAndParseConfig } from '@/domains/project/check-config'
import { CONFIG_FILE_DEFAULT, findConfig } from '@/domains/project/helpers'
import { createError, displayError } from '@/errors'
import { output } from '@/helpers/std-out'

export const UpgradeCommand = () => {
  const cmd = new Command('upgrade')

  cmd.description('Upgrade scalar project')
  cmd.argument(
    '[config]',
    'Path to the Scalar configuration file (usually `scalar.config.json5` or `scalar.config.json`)',
  )
  cmd.action(async (__configPath) => {
    const configPath =
      __configPath ||
      findConfig() ||
      (await text({
        message: 'Where is your configuration file located?',
        defaultValue: CONFIG_FILE_DEFAULT,
        placeholder: CONFIG_FILE_DEFAULT,
      }))

    // Validate the configuration file first
    const parseConfigResult = readAndParseConfig(configPath)

    if (!parseConfigResult.success) {
      const error = parseConfigResult.error
      if (error.type === 'file-not-found') {
        return output
          .error()
          .title('Scalar configuration could not be found')
          .message('Could not parse the Scalar Configuration file:')
          .message(error.inputPath)
          .exit('error')
      }

      if (error.type === 'parse-error') {
        return output
          .error()
          .title('Invalid configuration file')
          .message('Could not parse the Scalar Configuration file:')
          .message(error.filePath)
          .exit('error')
      }

      if (error.type === 'validation-error') {
        output
          .error()
          .title('The given Scalar Configuration is invalid.')
          .message(error.filePath)
          .print()

        if (error.issues) {
          output
            .error()
            .table(
              error.issues.map((issue) => [
                as.yellow(`${issue.path.join('/').trim() || 'root'}:`),
                issue.message,
              ]),
              { padding: 2 },
            )
            .print()
        }
      }

      return output.error().exit('error')
    }

    if (parseConfigResult.success) {
      output
        .info()
        .line(
          `${as.green('[SUCCESS]')} ${as.green('Configuration is valid:')} ${as.green(parseConfigResult.filePath)}`,
        )
        .print()
    }

    const { config } = parseConfigResult

    if (config.version === ConfigVersion.V2) {
      return output
        .info()
        .title('Your Scalar configuration is already upgraded')
        .message(
          'You are already on the latest Scalar docs version. No further action required.',
        )
        .exit('success')
    }

    const upgradedConfigResult = n.fromUnsafe(
      () => legacyTo2_0_0(config),
      (originalError) =>
        createError('FAILED_TO_UPGRADE_CONFIG_V1', originalError),
    )

    if (!upgradedConfigResult.success) {
      return output
        .error()
        .title('Could not upgrade config.')
        .message(upgradedConfigResult.error.code)
        .message(displayError(upgradedConfigResult.error.detail))
        .exit('error')
    }

    const { upgradedConfig, legacyTheme, legacyFooter, legacyScript } =
      upgradedConfigResult.data

    if (legacyTheme) {
      const writeUpgradedThemeResult = await n.fromUnsafe(
        () =>
          fs.writeFile(
            path.resolve(
              path.dirname(parseConfigResult.filePath),
              config.root ?? './',
              legacyTheme.path,
            ),
            legacyTheme.cssString,
          ),
        (originalError) =>
          createError('FAILED_TO_WRITE_UPGRADED_THEME', originalError),
      )

      if (!writeUpgradedThemeResult.success) {
        return output
          .error()
          .title('Could not save upgraded theme.')
          .message(writeUpgradedThemeResult.error.code)
          .message(displayError(writeUpgradedThemeResult.error.detail))
          .exit('error')
      }
    }

    if (legacyFooter) {
      const writeUpgradedFooterResult = await n.fromUnsafe(
        () =>
          fs.writeFile(
            path.resolve(
              path.dirname(parseConfigResult.filePath),
              config.root ?? './',
              legacyFooter.path,
            ),
            legacyFooter.footerString,
          ),
        (originalError) =>
          createError('FAILED_TO_WRITE_UPGRADED_FOOTER', originalError),
      )

      if (!writeUpgradedFooterResult.success) {
        return output
          .error()
          .title('Could not save upgraded footer.')
          .message(writeUpgradedFooterResult.error.code)
          .message(displayError(writeUpgradedFooterResult.error.detail))
          .exit('error')
      }
    }

    if (legacyScript) {
      const writeUpgradedScriptResult = await n.fromUnsafe(
        () =>
          fs.writeFile(
            path.resolve(
              path.dirname(parseConfigResult.filePath),
              config.root ?? './',
              legacyScript.path,
            ),
            legacyScript.scriptString,
          ),
        (originalError) =>
          createError('FAILED_TO_WRITE_UPGRADED_SCRIPT', originalError),
      )

      if (!writeUpgradedScriptResult.success) {
        return output
          .error()
          .title('Could not save upgraded head script.')
          .message(writeUpgradedScriptResult.error.code)
          .message(displayError(writeUpgradedScriptResult.error.detail))
          .exit('error')
      }
    }

    const writeUpgradedConfigResult = await n.fromUnsafe(
      () =>
        fs.writeFile(
          parseConfigResult.filePath,
          JSON.stringify(upgradedConfig, null, 2),
        ),
      (originalError) =>
        createError('FAILED_TO_WRITE_UPGRADED_CONFIG', originalError),
    )

    if (!writeUpgradedConfigResult.success) {
      return output
        .error()
        .title('Could not save upgraded config.')
        .message(writeUpgradedConfigResult.error.code)
        .message(displayError(writeUpgradedConfigResult.error.detail))
        .exit('error')
    }

    return output
      .info()
      .title('Successfully upgraded Scalar Docs configuration!')
      .message('Run `scalar project preview` to preview your project')
      .exit('success')
  })

  return cmd
}
